import {
  Box,
  Container,
  Heading,
  QUERY_DISTRIBUTIONS,
  VARIABLES_DISTRIBUTIONS,
  captureException,
  useSearchNft,
} from '@dapperlabs/core-fe';
import { getCookie } from 'cookies-next';
import { isFuture, isPast, parseISO } from 'date-fns';
import { flatten } from 'lodash';
import { GetServerSidePropsContext, NextPage } from 'next';
import React from 'react';
import { useSession } from 'src/edge/session';
import Head from 'src/general/components/Head/Head';
import { starterPurchaseLimit } from 'src/general/constants/config';
import { createApolloClient } from 'src/lib/apollo';
import {
  getContentfulClient,
  getContentfulEntryID,
} from 'src/lib/contentful/utils';
import { useFeatureFlags } from 'src/lib/launchDarkly';
import { useTranslation } from 'src/lib/translations';
import {
  ActiveDrop,
  PreviousPackList,
  UpcomingDrop,
  StarterPacks,
} from 'src/modules/DropsPage';
import BannerImg from 'src/modules/Layout/assets/png/drops_page_banner.png';
import {
  searchPackNftByOwnerAddress,
  SEARCH_PACK_NFT_BY_OWNER_ADDRESS,
} from 'src/modules/Queries/searchNft';

interface DropPageProps {
  activeDrop?: any;
  showStarterPacksFirst?: boolean;
  starterPacks?: any;
  title?: string;
  upcomingDrop?: any;
}

const DropPage = ({
  activeDrop,
  upcomingDrop,
  starterPacks,
  showStarterPacksFirst,
  title,
}: DropPageProps) => {
  const { t } = useTranslation();
  const { state: sessionState } = useSession();

  const flowAddress = sessionState?.context?.profile?.flowAddress;
  const { moments: buyerPacksList, state } = useSearchNft({
    aggregationResponseType: 'searchPackNft',
    query: SEARCH_PACK_NFT_BY_OWNER_ADDRESS(),
    variables: searchPackNftByOwnerAddress(flowAddress),
  });

  const isLoading = state.value === 'LOADING';
  const hasPurchaseLimit = starterPurchaseLimit > 0;
  const isOverPurchaseLimit =
    hasPurchaseLimit &&
    flowAddress &&
    buyerPacksList.length >= starterPurchaseLimit;

  const starterSection = starterPacks && !isOverPurchaseLimit && !isLoading && (
    <Container maxW={{ base: '100%', lg: '1440px' }} mt={0} p={0}>
      <StarterPacks
        description={starterPacks.description}
        distributions={starterPacks.distributions}
        imageSrc={starterPacks.image?.fields.file.url}
        title={starterPacks.heading}
      />
    </Container>
  );

  const activeSection = activeDrop && (
    <Box
      bgImage={BannerImg.src}
      bgSize='100% 189px'
      bgPosition='center top'
      bgRepeat='no-repeat'
    >
      <Container maxW={{ base: '100%', lg: '1440px' }} mt={0} p={0}>
        <ActiveDrop
          description={activeDrop.description}
          distributions={activeDrop.distributions}
          title={activeDrop.heading}
        />
      </Container>
    </Box>
  );

  return (
    <>
      <Head subtitle={title} title='' />
      <Box>
        {showStarterPacksFirst ? (
          <>
            {starterSection}
            {activeSection}
          </>
        ) : (
          <>
            {activeSection}
            {starterSection}
          </>
        )}
        <Container
          maxW={{ base: '100%', lg: '1440px' }}
          mt={{ base: 2, sm: 4 }}
          p={0}
        >
          {upcomingDrop && (
            <Box mb={14}>
              <UpcomingDrop {...upcomingDrop} />
            </Box>
          )}
          <Box as='section' mt={5} mb={24} mx={{ base: 4, lg: 24 }}>
            <Heading as='h2' mb={6}>
              {t('UpcomingDrops.PreviousPacks')}
            </Heading>
            <PreviousPackList />
          </Box>
        </Container>
      </Box>
    </>
  );
};

export const getDropsData = async ({
  contentfulClient,
  apolloClient,
  locale,
}) => {
  const dataDrops = await contentfulClient.getEntries({
    include: 4,
    locale,
    'sys.id': getContentfulEntryID('UPCOMING_DROPSPAGE'),
  });
  const entry = dataDrops?.items?.[0]?.fields || [];

  const title = entry.title;

  const dropContent = entry?.sections.filter(
    (section) => section.sys.contentType?.sys.id === 'dropContent',
  );

  const starterPacksIndex = entry?.sections.findIndex(
    (section) => section.sys.contentType?.sys.id === 'starterPacks',
  );

  let starterPacks =
    starterPacksIndex >= 0 ? entry?.sections[starterPacksIndex]?.fields : null;

  // Format response to extract ids for querying
  // distribution all needed data. This can include starter
  // packs, active or upcoming drops.
  let dropsList = dropContent.map((element) => [...element.fields.dropItems]);
  dropsList = flatten(dropsList).map((element) => element.fields);

  // Append starter Packs to the dropList
  if (starterPacks) {
    dropsList = [
      ...dropsList,
      ...starterPacks.dropItems.map((item) => ({ ...item.fields })),
    ];
  }

  const packIdStringArray =
    dropsList && dropsList.length > 0
      ? dropsList.map((drop: any) => drop.distributionId.toString())
      : [];

  const distributionsResponse = await apolloClient.query({
    context: {
      clientName: 'platformAPI',
    },
    errorPolicy: 'ignore',
    query: QUERY_DISTRIBUTIONS,
    variables: VARIABLES_DISTRIBUTIONS({
      byIDs: packIdStringArray,
      first: 1000,
    }),
  });

  const distributions =
    distributionsResponse?.data?.searchDistributions?.edges.map(
      (edge) => edge?.node,
    );

  // Merge Distribution data into correct section (active/upcoming)
  // Map Guarantee Label into distributions
  const sections = dropContent.map((section) => {
    const distIds = section.fields.dropItems.map((item) =>
      Number(item.fields.distributionId),
    );

    return {
      ...section,
      fields: {
        ...section.fields,
        distributions: distributions
          .filter((dist) => distIds.includes(Number(dist.id)))
          .map((dist) => {
            const dropsListItem = dropsList.find(
              (dropList) => Number(dist.id) === Number(dropList.distributionId),
            );
            return {
              ...dist,
              guaranteeLabel: dropsListItem?.label?.trim() || '',
              totalPacks: dropsListItem?.totalPacks || null,
            };
          }),
      },
    };
  });

  // Sort distributions by drop time ASC
  sections.forEach((section) => {
    if (section.fields.distributions.length > 1) {
      section.fields.distributions.sort(
        (distA, distB) =>
          new Date(distA.startTime).getTime() -
          new Date(distB.startTime).getTime(),
      );
    }
  });

  // extract data from each section
  const activeDrop =
    sections.find((section) => isPast(parseISO(section.fields?.dropDate)))
      ?.fields || null;
  const upcomingDrop =
    sections.find((section) => isFuture(parseISO(section.fields?.dropDate)))
      ?.fields || null;

  // Map distributions to starter Packs
  if (starterPacks) {
    const starterPackDistIds = starterPacks.dropItems.map((item) =>
      Number(item.fields.distributionId),
    );

    starterPacks = {
      ...starterPacks,
      distributions: distributions
        .filter((dist) => starterPackDistIds.includes(Number(dist.id)))
        .map((dist) => {
          const dropsListItem = dropsList.find(
            (dropList) => Number(dist.id) === Number(dropList.distributionId),
          );
          return {
            ...dist,
            guaranteeLabel: dropsListItem?.label?.trim() || '',
            totalPacks: dropsListItem?.totalPacks || null,
          };
        }),
    };
  }
  return {
    activeDrop: activeDrop || null,
    showStarterPacksFirst: starterPacksIndex === 0,
    starterPacks: starterPacks || null,
    title: title || '',
    upcomingDrop: upcomingDrop || null,
  };
};

export async function getServerSideProps({
  req,
  res,
}: GetServerSidePropsContext) {
  try {
    const locale = getCookie('language', { req, res }) || 'en-US';
    const apolloClient = createApolloClient();
    const contentfulClient = getContentfulClient();
    const {
      activeDrop,
      starterPacks,
      upcomingDrop,
      title,
      showStarterPacksFirst,
    } = await getDropsData({
      apolloClient,
      contentfulClient,
      locale,
    });
    return {
      props: {
        activeDrop,
        showStarterPacksFirst,
        starterPacks,
        title,
        upcomingDrop,
      },
    };
  } catch (error) {
    await captureException(error, { req, res });
    return {
      props: {
        activeDrop: null,
        showStarterPacksFirst: true,
        starterPacks: null,
        title: '',
        upcomingDrop: null,
      },
    };
  }
}

const DropPagePresenter: NextPage = ({
  error,
  ...props
}: {
  error?: boolean;
}) => {
  const { isDropsEnabled } = useFeatureFlags(); // @TODO: what flag do we need here

  if (!isDropsEnabled) return null;
  if (error) return <>Unable to load drop details.</>;
  return <DropPage {...props} />;
};

export default DropPagePresenter;
